import PropTypes, { bool, func } from "prop-types";

import styled from "styled-components/macro";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrFade from "UI/FnrFade";
import FnrTypography from "UI/FnrTypography";
import { colors } from "UI/colors";

const preventDefaultMouseDown = (e) => e.preventDefault();

/**
 * "We standardized your address formatting." undo callout — one model across every surface
 * (§2): the reformat has already applied (in place, or revealed post-save), and this offers to
 * revert it. Undo re-persists exactly what the user saw (their own spelling), marked
 * unverifiable, and on collapse-in-place surfaces trips the breaker so it can't immediately
 * re-collapse.
 *
 * Undo is the ONLY control by design — no dismiss/confirm. The standardization is already
 * saved, so doing nothing IS the accept path; a "confirm"/"close" button would reintroduce the
 * rejected confirm step and read as a demand, and the past-tense heading stays ambient for the
 * same reason. A surface needing its own close supplies it on its shell chrome, not here.
 * Does no positioning of its own — the surface places it.
 */
export default function StandardizationNotice({
  open,
  onUndo,
  flush,
  bare,
  align,
  loading,
  minHeight,
}) {
  return (
    // inertWhileAnimating: keeps the undo button from flicking :hover as the pill
    // slides under the cursor on reveal — see FnrFade.
    <FnrFade show={open} inertWhileAnimating>
      <Wrapper
        role="region"
        aria-label="address-standardized"
        $flush={flush}
        $bare={bare}
        $align={align}
        $minHeight={minHeight}
      >
        <FnrContainedIcon
          icon="wand-magic-sparkles fas"
          size="sm"
          noBackgroundColor
          color="fnBlue"
        />
        <TextSlot>
          <FnrTypography
            type="copy"
            fontSize="sm"
            color="textSecondaryDark"
            lineHeight={1.2}
          >
            We standardized your address formatting.
          </FnrTypography>
        </TextSlot>
        <UndoButton
          type="button"
          onClick={onUndo}
          onMouseDown={preventDefaultMouseDown}
          aria-label="undo address standardization"
          disabled={loading}
          aria-busy={loading || undefined}
        >
          <FnrContainedIcon
            icon="undo fas"
            size="sm"
            noBackgroundColor
            noPadding
            color="currentColor"
            isLoading={loading}
          />
        </UndoButton>
      </Wrapper>
    </FnrFade>
  );
}

StandardizationNotice.propTypes = {
  open: bool,
  // Re-persist what the user saw (their own spelling), marked unverifiable.
  onUndo: func,
  // Drops the top margin used for inline (below-row) placement so the notice sits flush in a popover.
  flush: bool,
  // Strips the pill chrome (background + border) so content floats on a parent that already supplies one.
  bare: bool,
  // Anything other than "left" (incl. unset) centers; inline surfaces left-align, the boards popover centers.
  align: PropTypes.oneOf(["left", "center"]),
  // While committing: swaps the undo icon for a spinner and disables the button against re-trigger.
  loading: bool,
  // Override min-height (px) to force a corner radius (min-height/2) matching the container (e.g. 48 → 24px corner).
  minHeight: PropTypes.number,
};

// Full-round pill. NEST_GAP is the gap between the undo button's circle and the pill border;
// because the button is a circle nested inside the pill's end-cap arc, that gap stays constant
// at any pill height (one line or three).
const NEST_GAP = 2;
const BUTTON_SIZE = 28;

const Wrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  max-width: 100%;
  margin-left: ${({ $align }) => ($align === "left" ? "0" : "auto")};
  margin-right: auto;
  margin-top: ${({ $flush }) => ($flush ? "0px" : "8px")};
  /* Near-uniform 2px: the leading icon's 32px box and the trailing button bring their own
     breathing room; the left edge gets a touch more (4px) so the icon clears the pill curve. */
  padding: ${NEST_GAP}px;
  padding-left: 4px;
  /* One-line floor: the undo circle + room. BUTTON_SIZE + 2*NEST_GAP + 2px border (1px top +
     1px bottom). Callers may override via $minHeight (the "both" slide passes 48 so the 999px
     radius resolves to a 24px corner matching the popover shell). */
  min-height: ${({ $minHeight }) =>
    $minHeight != null
      ? `${$minHeight}px`
      : `${BUTTON_SIZE + NEST_GAP * 2 + 2}px`};
  border: 1px solid ${({ $bare }) => ($bare ? "transparent" : colors.fnBlue200)};
  border-radius: 999px;
  background: ${({ $bare }) => ($bare ? "transparent" : colors.fnBlue100)};
  transition: background 200ms, border-color 200ms;
`;

// Flex column blockifies the typography so its own lineHeight governs the line boxes; a bare
// inline would ride this slot's 16px strut instead.
const TextSlot = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

// Icon button on FnrButtonWrapper (stays mounted while loading — a spinner swaps in for the
// glyph). Bare blue glyph that fills solid blue on hover; the icon's color="currentColor" means
// flipping the button color to white on hover carries the glyph with it.
const UndoButton = styled(FnrButtonWrapper)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${BUTTON_SIZE}px;
  height: ${BUTTON_SIZE}px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${colors.fnBlue};
  // Only background is transitioned; the glyph eases its color over FnrContainedIcon's internal
  // 300ms transition, which matches the background's 300ms so they animate in lockstep.
  transition: background-color 300ms ease, transform 100ms;
  &:hover,
  &:focus-visible {
    background: ${colors.fnBlue};
    color: ${colors.white};
  }
  &:active {
    transform: scale(0.92);
  }
  /* The fa-undo glyph's ink sits off-center inside its (flexbox-centered) advance box —
     invisible on the bare icon, but it reads as drifting left once the circle fills solid on
     hover/focus, so a small optical translateX corrects the ink (not a layout change). */
  & .fa-undo {
    transform: translateX(0.5px);
  }
`;
